/**
 * Rdzen pomiaru prozodii. JEDYNA implementacja w projekcie.
 *
 * Ten plik dziala po obu stronach: w przegladarce (AudioWorklet, zywy podglad
 * tonu i glosnosci) oraz w Node (analiza wgranego nagrania). Dlatego nie ma tu
 * ani jednego importu, zadnego odwolania do DOM ani do API Node. Wylacznie
 * czyste funkcje nad Float32Array. Kazda zmiana tutaj dotyczy obu srodowisk
 * naraz i to jest cel - inaczej liczba pokazana na zywo rozjechalaby sie
 * z liczba w raporcie.
 *
 * Podzial obowiazkow w calym produkcie jest twardy: to, co da sie zmierzyc,
 * mierzymy tutaj, deterministycznie i powtarzalnie. To, co trzeba ocenic,
 * ocenia model - osobno i z jawnie zaznaczona niepewnoscia. Eksperyment
 * opisany w docs/eksperyment-emocje.md pokazal, ze model nie nadaje sie do
 * orzekania o tonie glosu, wiec ten plik musi udzwignac to sam.
 *
 * Zasada nadrzedna: NIGDY nie zwracamy liczby, ktorej nie ufamy. Kazda metryka
 * moze byc null, a kazdy wynik niesie flagi jakosci. Zgadnieta wartosc jest
 * gorsza niz brak wartosci, bo uzytkownik nie ma jak jej podwazyc.
 */

// ---------------------------------------------------------------------------
// Stale. Kazda z uzasadnieniem i z oznaczeniem, czy jest pewna, czy do kalibracji.
// ---------------------------------------------------------------------------

/**
 * Ponizej tej dlugosci cisza to zwarcie gloski p, t, k, b, d, g, a nie pauza.
 * Fonetyka podaje zakres 150-250 ms, bierzemy srodek. [PEWNE]
 */
export const PAUSE_MIN_MS = 200;

/**
 * Zakres poszukiwan czestotliwosci podstawowej. Ponizej 60 Hz to praktycznie
 * zawsze blad oktawowy w dol, powyzej 450 Hz mowa biznesowa nie siega. [PEWNE]
 */
export const F0_MIN_HZ = 60;
export const F0_MAX_HZ = 450;

/** Prog z oryginalnej pracy o YIN (de Cheveigne i Kawahara, 2002). [PEWNE] */
export const YIN_THRESHOLD = 0.15;

/**
 * BYLA tu stala YIN_MIN_PROBABILITY = 0.45, uzywana jako druga bramka dzwiecznosci.
 * Nie robila NIC i zostala usunieta, a nie poprawiona - oto dlaczego.
 *
 * estimateF0Yin ustawia bestTau wylacznie wtedy, gdy znormalizowana roznica
 * spadnie PONIZEJ YIN_THRESHOLD (0,15), i schodzi jeszcze nizej, do dna minimum.
 * Zwracane `probability` to `1 - normalized[bestTau]`, wiec jest zawsze WIEKSZE
 * niz 0,85. Warunek `probability >= 0.45` byl spelniony przy kazdym niepustym
 * wyniku - kod wygladal na bramke wiarygodnosci, a przepuszczal wszystko.
 *
 * O dzwiecznosci ramki rozstrzyga zatem SAM YIN_THRESHOLD: brak minimum ponizej
 * progu daje null i ramka jest bezdzwieczna. Podniesienie usunietej stalej
 * powyzej 0,85 zmienialoby pomiar i wymaga kalibracji na wlasnych nagraniach,
 * a nie zgadniecia - dlatego tego nie zrobiono przy okazji sprzatania.
 *
 * OGRANICZENIE, ktore z tego wynika i ktore trzeba znac: YIN wykrywa OKRESOWOSC,
 * nie mowe. Czysty ton, brzeczenie zasilacza czy muzyka w tle podnosza
 * voicedRatio tak samo jak glos. Bramka minVoicedRatio chroni przed cisza,
 * nie przed dzwiekiem, ktory mowa nie jest.
 */

/** Podloga RMS. Kontrakt: nigdy nie zwracamy -Infinity. [PEWNE] */
export const RMS_FLOOR_DBFS = -120;

/** Prog, powyzej ktorego probka jest uznana za przesterowana. [PEWNE] */
export const CLIPPING_SAMPLE_LEVEL = 0.99;

/** Bramki jakosci. Ponizej ktoregokolwiek progu odmawiamy podania liczby. */
export const GATES = {
  /** Przy czterech sylabach pomylka o jedna to blad tempa o 25 procent. [PEWNE] */
  minPhonationSec: 0.6,
  minSyllablesForRate: 6,
  /** Bez dzwiecznych ramek nie ma czego mierzyc przy tonie. [PEWNE] */
  minVoicedSec: 0.3,
  minVoicedRatio: 0.2,
  /** Ponizej tego poziomu mowa ginie w szumie kwantyzacji. [DO KALIBRACJI] */
  minMedianSpeechDbfs: -45,
  maxClippingRatio: 0.01,
} as const;

/**
 * Najdluzsza wypowiedz (sekundy), dla ktorej liczymy prozodie. Dluzsza dostaje flage TOO_LONG i pomiar
 * "za malo danych" (kazda metryka null), a jej probki NIE sa wycinane do pamieci.
 *
 * DLACZEGO 10 MINUT (zmierzone 05.10.2026, scratch/pomiar-dlugiej-wypowiedzi.ts, jedna wypowiedz, 16 kHz):
 *
 *   dlugosc   okno w pamieci   szczyt RSS po pomiarze i WAV (start procesu: ok. 92 MiB)
 *   5 min     18 MiB           ok. 144 MiB
 *   15 min    55 MiB           ok. 221 MiB
 *   30 min    110 MiB          ok. 349 MiB
 *   60 min    220 MiB          ok. 563 MiB, a pomiar prozodii RZUCA RangeError: detectPauses rozwija
 *                              Math.max(...ramki), a ramek jest 50 na sekunde. Zmierzony prog tego stosu to
 *                              ok. 120 tys. ramek, czyli ok. 40 minut jednej wypowiedzi.
 *
 * Pamiec rosnie liniowo (ok. 8,6 MiB na minute wypowiedzi: okno, kopia przy wycinaniu, WAV i tablice pomiaru),
 * wiec przy limicie 10 minut wypowiedz kosztuje ok. 86 MiB ponad stan wyjsciowy, niezaleznie od dlugosci
 * nagrania. Powyzej ok. 40 minut pomiar w ogole nie dziala, a prog zalezy od rozmiaru stosu, wiec limit ma
 * duzy zapas. Drugi powod jest merytoryczny: tempo i rozpietosc tonu usrednione z godziny jednej wypowiedzi
 * (wyklad, monolog) nie opisuja "sposobu mowienia" w zadnym sensie, na ktorym uzytkownik moglby cos zbudowac.
 * Zasada nadrzedna: nie pokazujemy liczby, ktorej nie ufamy.
 *
 * Wartosc jest wybrana, nie wyliczona: do zmiany po kalibracji na prawdziwych nagraniach.
 */
export const MAX_OKNO_POMIARU_SEK = 10 * 60;

export type QualityFlag =
  | "TOO_SHORT"
  | "TOO_LONG"
  | "TOO_QUIET"
  | "CLIPPING"
  | "NO_VOICED"
  | "LOW_VOICED_RATIO"
  | "BAND_LIMITED"
  | "TOO_FEW_SYLLABLES";

// ---------------------------------------------------------------------------
// Poziom sygnalu
// ---------------------------------------------------------------------------

/** Wartosc skuteczna sygnalu w dBFS. Cisza zwraca podloge, nie minus nieskonczonosc. */
export function rmsDbfs(frame: Float32Array): number {
  if (frame.length === 0) return RMS_FLOOR_DBFS;

  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];

  const rms = Math.sqrt(sum / frame.length);
  if (rms <= 0) return RMS_FLOOR_DBFS;

  return Math.max(RMS_FLOOR_DBFS, 20 * Math.log10(rms));
}

/** Udzial probek na granicy skali. Wysoki oznacza przesterowanie i falszywe F0. */
export function clippingRatio(samples: Float32Array): number {
  if (samples.length === 0) return 0;

  let clipped = 0;
  for (let i = 0; i < samples.length; i++) {
    if (Math.abs(samples[i]) >= CLIPPING_SAMPLE_LEVEL) clipped++;
  }
  return clipped / samples.length;
}

// ---------------------------------------------------------------------------
// Czestotliwosc podstawowa - YIN
// ---------------------------------------------------------------------------

export interface F0Estimate {
  hz: number;
  /** 1 minus znormalizowana roznica w minimum. Wyzej znaczy pewniej. */
  probability: number;
}

/**
 * Estymacja F0 metoda YIN.
 *
 * Kroki zgodnie z oryginalna praca:
 *   1. funkcja roznicowa d(tau)
 *   2. skumulowana srednia znormalizowana d'(tau), z d'(0) = 1
 *   3. pierwsze minimum lokalne ponizej progu (a nie globalne minimum -
 *      globalne czesto laduje na wielokrotnosci okresu, czyli oktawe nizej)
 *   4. interpolacja paraboliczna wokol minimum
 *
 * Zwraca null, gdy ramka jest za krotka albo nie ma w niej okresowosci.
 * To NIE jest sytuacja wyjatkowa - polowa ramek w mowie jest bezdzwieczna.
 */
export function estimateF0Yin(
  frame: Float32Array,
  sampleRate: number,
  options: { minHz?: number; maxHz?: number; threshold?: number } = {},
): F0Estimate | null {
  const minHz = options.minHz ?? F0_MIN_HZ;
  const maxHz = options.maxHz ?? F0_MAX_HZ;
  const threshold = options.threshold ?? YIN_THRESHOLD;

  const minTau = Math.max(2, Math.floor(sampleRate / maxHz));
  const maxTau = Math.floor(sampleRate / minHz);

  // Potrzebujemy dwoch pelnych okresow najnizszej szukanej czestotliwosci,
  // inaczej funkcja roznicowa nie ma na czym pracowac przy duzych tau.
  if (frame.length < maxTau * 2 || maxTau <= minTau) return null;

  const halfLength = Math.floor(frame.length / 2);
  const diff = new Float32Array(maxTau + 1);

  for (let tau = minTau; tau <= maxTau; tau++) {
    let sum = 0;
    for (let i = 0; i < halfLength; i++) {
      const delta = frame[i] - frame[i + tau];
      sum += delta * delta;
    }
    diff[tau] = sum;
  }

  // Skumulowana srednia znormalizowana. Bez niej d(tau) maleje monotonicznie
  // i kazdy prog absolutny bylby bez sensu.
  const normalized = new Float32Array(maxTau + 1);
  normalized[0] = 1;
  let runningSum = 0;
  for (let tau = minTau; tau <= maxTau; tau++) {
    runningSum += diff[tau];
    normalized[tau] = runningSum === 0 ? 1 : (diff[tau] * (tau - minTau + 1)) / runningSum;
  }

  let bestTau = -1;
  for (let tau = minTau; tau < maxTau; tau++) {
    if (normalized[tau] < threshold) {
      // Zjedz w dol do dna tego minimum, zamiast brac pierwsze przekroczenie progu.
      let t = tau;
      while (t + 1 <= maxTau && normalized[t + 1] < normalized[t]) t++;
      bestTau = t;
      break;
    }
  }

  if (bestTau === -1) return null;

  const refinedTau = parabolicRefine(normalized, bestTau, minTau, maxTau);
  const hz = sampleRate / refinedTau;
  if (hz < minHz || hz > maxHz) return null;

  return { hz, probability: Math.max(0, Math.min(1, 1 - normalized[bestTau])) };
}

/**
 * Interpolacja paraboliczna przez trzy punkty wokol minimum. Bez niej
 * rozdzielczosc F0 jest ograniczona krokiem probkowania, co przy 16 kHz
 * i glosie 200 Hz daje skok rzedu 2,5 Hz - widoczny jako schodki na wykresie.
 */
function parabolicRefine(values: Float32Array, tau: number, minTau: number, maxTau: number): number {
  if (tau <= minTau || tau >= maxTau) return tau;

  const prev = values[tau - 1];
  const curr = values[tau];
  const next = values[tau + 1];
  const denominator = 2 * (2 * curr - next - prev);
  if (denominator === 0) return tau;

  const shift = (next - prev) / denominator;
  // Przesuniecie powyzej pol kroku oznacza, ze parabola nie opisuje tego minimum.
  return Math.abs(shift) > 0.5 ? tau : tau + shift;
}

/**
 * Udzial energii ponizej progu odciecia, liczony filtrem jednobiegunowym.
 *
 * Sluzy do wykrycia nagrania z obcietym dolem - tor telefoniczny przepuszcza
 * 300-3400 Hz, wiec czestotliwosc podstawowa niskich glosow meskich jest w nim
 * wytlumiona. Nie zmieniamy z tego powodu zmierzonej wartosci, tylko oznaczamy
 * nagranie flaga, zeby dalo sie to pokazac uzytkownikowi.
 *
 * Filtr jest kaskada czterech stopni jednobiegunowych. Jeden stopien ma zbocze
 * 6 dB na oktawe i to za malo: ton 1200 Hz, czyli dwie oktawy nad progiem,
 * przechodzil przez niego z wynikiem 0,245 i nie dalo sie go odroznic od mowy
 * pelnopasmowej. Cztery stopnie daja 24 dB na oktawe i czysta separacje.
 *
 * To nadal nie jest filtr do obrobki dzwieku, tylko do postawienia flagi -
 * ale nie wymaga FFT, ktore musialoby dzialac identycznie w przegladarce i w Node.
 */
const BAND_FILTER_STAGES = 4;

export function lowBandEnergyRatio(samples: Float32Array, sampleRate: number, cutoffHz = 300): number {
  if (samples.length === 0) return 0;

  const alpha = 1 - Math.exp((-2 * Math.PI * cutoffHz) / sampleRate);
  const stages = new Float64Array(BAND_FILTER_STAGES);
  let lowEnergy = 0;
  let totalEnergy = 0;

  for (let i = 0; i < samples.length; i++) {
    let value = samples[i];
    for (let s = 0; s < BAND_FILTER_STAGES; s++) {
      stages[s] += alpha * (value - stages[s]);
      value = stages[s];
    }
    lowEnergy += value * value;
    totalEnergy += samples[i] * samples[i];
  }

  if (totalEnergy <= 0) return 0;
  return Math.sqrt(lowEnergy / totalEnergy);
}

/**
 * Ponizej tego udzialu energii w dole pasma uznajemy nagranie za obciete
 * i oznaczamy flaga BAND_LIMITED. [DO KALIBRACJI na wlasnych nagraniach]
 */
export const BAND_LIMIT_RATIO = 0.15;

/**
 * DLACZEGO NIE MA TU KOREKTY BLEDU OKTAWOWEGO
 *
 * Pierwsza wersja tego pliku probowala wykrywac przypadek, w ktorym YIN lapie
 * pierwsza harmoniczna zamiast czestotliwosci podstawowej, i dzielic wynik przez
 * dwa. Testy pokazaly, ze taka korekta psuje poprawne pomiary: sygnal okresowy
 * o czestotliwosci 180 Hz powtarza sie takze co dwa okresy, wiec minimum przy
 * 90 Hz istnieje ZAWSZE, dla kazdego dzwieku. Zabezpieczenie samo wprowadzalo
 * blad, przed ktorym mialo chronic - 180 Hz zamienialo w 90 Hz.
 *
 * Glebszy powod, dla ktorego korekta jest zbedna: YIN jest odporny na brak
 * czestotliwosci podstawowej z samej konstrukcji. Skumulowana srednia
 * znormalizowana istnieje wlasnie po to. Przefiltrowany glos meski nadal ma
 * okresowosc 110 Hz zapisana w rozkladzie harmonicznych 220, 330 i 440 Hz,
 * a funkcja roznicowa czyta okresowosc przebiegu, nie widmo.
 *
 * Zostaje wiec wykrycie samego warunku i uczciwa flaga zamiast po cichu
 * podmienionej liczby.
 */

// ---------------------------------------------------------------------------
// Przebieg w czasie
// ---------------------------------------------------------------------------

export interface FrameAnalysis {
  startSec: number;
  rmsDb: number;
  f0Hz: number | null;
  voiced: boolean;
}

export interface FrameOptions {
  /** Dlugosc okna analizy. 40 ms miesci dwa okresy glosu 60 Hz. */
  frameMs?: number;
  /** Przesuniecie okna. 20 ms daje 50 pomiarow na sekunde. */
  hopMs?: number;
  /** Prog glosnosci, ponizej ktorego ramka to cisza, wzgledem szczytu nagrania. */
  silenceBelowPeakDb?: number;
}

/** Dzieli sygnal na ramki i liczy dla kazdej poziom oraz F0. */
export function analyzeFrames(
  samples: Float32Array,
  sampleRate: number,
  options: FrameOptions = {},
): FrameAnalysis[] {
  const frameMs = options.frameMs ?? 40;
  const hopMs = options.hopMs ?? 20;
  const frameSize = Math.floor((frameMs / 1000) * sampleRate);
  const hopSize = Math.floor((hopMs / 1000) * sampleRate);
  if (frameSize <= 0 || hopSize <= 0 || samples.length < frameSize) return [];

  const frames: FrameAnalysis[] = [];
  for (let start = 0; start + frameSize <= samples.length; start += hopSize) {
    const frame = samples.subarray(start, start + frameSize);
    const rmsDb = rmsDbfs(frame);
    const raw = estimateF0Yin(frame, sampleRate);

    // Sam wynik YIN rozstrzyga o dzwiecznosci: null znaczy "brak okresowosci
    // ponizej progu". Drugiej bramki tu nie ma, bo ta, ktora tu stala, byla
    // spelniona zawsze - powod przy usunietej stalej wyzej.
    let f0Hz: number | null = null;
    let voiced = false;
    if (raw) {
      f0Hz = raw.hz;
      voiced = true;
    }

    frames.push({ startSec: start / sampleRate, rmsDb, f0Hz, voiced });
  }
  return frames;
}

export interface Pause {
  startSec: number;
  durationSec: number;
}

/**
 * Znajduje pauzy - ciagi cichych ramek dluzsze niz PAUSE_MIN_MS.
 *
 * Prog ciszy jest WZGLEDNY wobec szczytu nagrania, a nie absolutny. Nagranie
 * zrobione cicho ma inna podloge niz zrobione glosno, a pauza to zjawisko
 * wzgledne: brak mowy tam, gdzie przed chwila mowa byla.
 */
export function detectPauses(frames: FrameAnalysis[], options: FrameOptions = {}): Pause[] {
  if (frames.length === 0) return [];

  const hopMs = options.hopMs ?? 20;
  const belowPeak = options.silenceBelowPeakDb ?? 25;
  // Petla zamiast Math.max(...ramki): rozwiniecie tablicy w argumenty przepelnia stos od ok. 120 tys. ramek (40 minut sygnalu).
  let peakDb = Number.NEGATIVE_INFINITY;
  for (const f of frames) if (f.rmsDb > peakDb) peakDb = f.rmsDb;
  const silenceThreshold = peakDb - belowPeak;

  const pauses: Pause[] = [];
  let runStart: number | null = null;

  const closeRun = (endIndex: number) => {
    if (runStart === null) return;
    const durationSec = ((endIndex - runStart) * hopMs) / 1000;
    if (durationSec * 1000 >= PAUSE_MIN_MS) {
      pauses.push({ startSec: frames[runStart].startSec, durationSec });
    }
    runStart = null;
  };

  frames.forEach((frame, i) => {
    const silent = frame.rmsDb < silenceThreshold && !frame.voiced;
    if (silent) {
      if (runStart === null) runStart = i;
    } else {
      closeRun(i);
    }
  });
  closeRun(frames.length);

  return pauses;
}

// ---------------------------------------------------------------------------
// Sylaby
// ---------------------------------------------------------------------------

const POLISH_VOWELS = new Set(["a", "ą", "e", "ę", "i", "o", "ó", "u", "y"]);

/**
 * Liczy sylaby w polskim tekscie.
 *
 * Sylaby, a nie slowa, sa metryka pierwszorzedna tempa. Polska morfologia daje
 * slowa bardzo roznej dlugosci ("nie" kontra "przygotowalismy"), wiec slowa na
 * minute mocno zaleza od tego, o czym akurat mowa. Sylaby na sekunde nie.
 *
 * Regula: kazda grupa samoglosek to jedna sylaba, z jednym wyjatkiem - "i"
 * bezposrednio przed inna samogloska jest znakiem miekkosci, nie osobna
 * sylaba ("zestawienie" to ze-sta-wie-nie, cztery, a nie szesc). Pozostale
 * sasiadujace samogloski licza sie osobno, bo w polskim rozziew jest normalny
 * ("nauka" to na-u-ka, trzy).
 */
export function countSyllablesPl(text: string): number {
  const lower = text.toLowerCase();
  let count = 0;
  let i = 0;

  while (i < lower.length) {
    const char = lower[i];
    if (!POLISH_VOWELS.has(char)) {
      i++;
      continue;
    }

    // "i" zlewa sie z nastepna samogloska w jedno jadro sylaby.
    if (char === "i" && i + 1 < lower.length && POLISH_VOWELS.has(lower[i + 1])) {
      count++;
      i += 2;
      continue;
    }

    count++;
    i++;
  }
  return count;
}

/** Liczy slowa. Interpunkcja i wielokrotne spacje nie tworza slow. */
export function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
}

// ---------------------------------------------------------------------------
// Podsumowanie
// ---------------------------------------------------------------------------

export interface ProsodySummary {
  durationSec: number;
  /** Czas z mowa, czyli bez pauz. Podstawa tempa artykulacji. */
  phonationSec: number;

  syllablesPerSecond: number | null;
  wordsPerMinute: number | null;
  /** Tempo liczone bez pauz. Odroznia "mowi wolno" od "duzo milczy". */
  articulationRate: number | null;

  pauseCount: number;
  pauseRatio: number;
  longestPauseMs: number;

  rmsDbMean: number | null;
  rmsDbP90: number | null;
  dynamicRangeDb: number | null;

  f0MedianHz: number | null;
  f0P10Hz: number | null;
  f0P90Hz: number | null;
  /** Rozpietosc w poltonach. Odroznia monotonnosc od zroznicowania. */
  f0RangeSemitones: number | null;
  f0CvPct: number | null;
  voicedRatio: number;

  flags: QualityFlag[];
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return Number.NaN;
  if (sorted.length === 1) return sorted[0];
  const index = (sorted.length - 1) * p;
  const low = Math.floor(index);
  const high = Math.ceil(index);
  if (low === high) return sorted[low];
  return sorted[low] + (sorted[high] - sorted[low]) * (index - low);
}

/** Odleglosc w poltonach miedzy dwiema czestotliwosciami. */
export function semitones(fromHz: number, toHz: number): number {
  return 12 * Math.log2(toHz / fromHz);
}

/**
 * Wynik dla wypowiedzi dluzszej niz MAX_OKNO_POMIARU_SEK: nic nie zmierzono, jest tylko flaga TOO_LONG.
 *
 * Zera w polach, ktore nie moga byc null (pauseCount, pauseRatio, voicedRatio, phonationSec...), NIE sa
 * pomiarem: to "brak danych" w typie, ktory nie zna null, tak samo jak przy pustym sygnale. Czyta sie je
 * tylko razem z flaga, a bramka pomiarNieufny (src/services/annotate.ts) wlacza TOO_LONG miedzy flagi, przy
 * ktorych zadna etykieta pomiaru sie nie pokazuje.
 *
 * Eksportowane, bo sciezka strumieniowa nie ma probek tej wypowiedzi (nie sa wycinane do pamieci) i sklada
 * wynik z samej dlugosci.
 */
export function prozodiaPominietaZaDlugo(durationSec: number): ProsodySummary {
  return {
    durationSec,
    phonationSec: 0,
    syllablesPerSecond: null,
    wordsPerMinute: null,
    articulationRate: null,
    pauseCount: 0,
    pauseRatio: 0,
    longestPauseMs: 0,
    rmsDbMean: null,
    rmsDbP90: null,
    dynamicRangeDb: null,
    f0MedianHz: null,
    f0P10Hz: null,
    f0P90Hz: null,
    f0RangeSemitones: null,
    f0CvPct: null,
    voicedRatio: 0,
    flags: ["TOO_LONG"],
  };
}

/**
 * Pelny pomiar jednego fragmentu mowy.
 *
 * `text` jest opcjonalny - bez niego nie da sie policzyc tempa, ale ton,
 * glosnosc i pauzy owszem. Kazda metryka, ktorej nie da sie policzyc wiarygodnie,
 * wychodzi jako null z odpowiednia flaga, nigdy jako zgadnieta liczba.
 *
 * Fragment dluzszy niz MAX_OKNO_POMIARU_SEK nie jest mierzony wcale: flaga TOO_LONG (patrz stala).
 */
export function summarizeProsody(
  samples: Float32Array,
  sampleRate: number,
  options: FrameOptions & { text?: string } = {},
): ProsodySummary {
  const flags: QualityFlag[] = [];
  const durationSec = samples.length / sampleRate;

  if (durationSec > MAX_OKNO_POMIARU_SEK) return prozodiaPominietaZaDlugo(durationSec);

  const frames = analyzeFrames(samples, sampleRate, options);
  const pauses = detectPauses(frames, options);
  const pausedSec = pauses.reduce((sum, p) => sum + p.durationSec, 0);
  const phonationSec = Math.max(0, durationSec - pausedSec);

  const voicedFrames = frames.filter((f) => f.voiced && f.f0Hz !== null);
  const voicedRatio = frames.length === 0 ? 0 : voicedFrames.length / frames.length;
  const voicedSec = voicedFrames.length * ((options.hopMs ?? 20) / 1000);

  if (durationSec < GATES.minPhonationSec) flags.push("TOO_SHORT");
  if (clippingRatio(samples) > GATES.maxClippingRatio) flags.push("CLIPPING");
  if (voicedFrames.length === 0) flags.push("NO_VOICED");
  else if (voicedRatio < GATES.minVoicedRatio || voicedSec < GATES.minVoicedSec) flags.push("LOW_VOICED_RATIO");

  // Obciety dol pasma. Nie zmieniamy z tego powodu zmierzonego tonu, ale
  // uzytkownik ma prawo wiedziec, ze nagranie telefoniczne jest gorszym
  // materialem do oceny wysokosci glosu niz nagranie z mikrofonu.
  if (samples.length > 0 && lowBandEnergyRatio(samples, sampleRate) < BAND_LIMIT_RATIO) {
    flags.push("BAND_LIMITED");
  }

  // Poziom liczymy tylko z ramek z mowa. Wliczenie ciszy zanizyloby wynik
  // tym bardziej, im wiecej ktos robi pauz, co nie ma nic wspolnego z glosnoscia.
  const speechDb = frames.filter((f) => f.voiced).map((f) => f.rmsDb).sort((a, b) => a - b);
  const rmsDbMean = speechDb.length ? speechDb.reduce((s, v) => s + v, 0) / speechDb.length : null;
  const rmsDbP90 = speechDb.length ? percentile(speechDb, 0.9) : null;
  const dynamicRangeDb = speechDb.length ? percentile(speechDb, 0.9) - percentile(speechDb, 0.1) : null;

  if (rmsDbMean !== null && rmsDbMean < GATES.minMedianSpeechDbfs) flags.push("TOO_QUIET");

  const canTrustF0 = voicedFrames.length > 0 && !flags.includes("LOW_VOICED_RATIO") && !flags.includes("NO_VOICED");
  const f0Values = voicedFrames.map((f) => f.f0Hz as number).sort((a, b) => a - b);

  const f0MedianHz = canTrustF0 ? percentile(f0Values, 0.5) : null;
  const f0P10Hz = canTrustF0 ? percentile(f0Values, 0.1) : null;
  const f0P90Hz = canTrustF0 ? percentile(f0Values, 0.9) : null;
  const f0RangeSemitones = f0P10Hz !== null && f0P90Hz !== null && f0P10Hz > 0 ? semitones(f0P10Hz, f0P90Hz) : null;

  let f0CvPct: number | null = null;
  if (canTrustF0 && f0Values.length > 1) {
    const mean = f0Values.reduce((s, v) => s + v, 0) / f0Values.length;
    const variance = f0Values.reduce((s, v) => s + (v - mean) ** 2, 0) / f0Values.length;
    f0CvPct = mean > 0 ? (Math.sqrt(variance) / mean) * 100 : null;
  }

  let syllablesPerSecond: number | null = null;
  let wordsPerMinute: number | null = null;
  let articulationRate: number | null = null;

  if (options.text !== undefined) {
    const syllables = countSyllablesPl(options.text);
    const words = countWords(options.text);

    if (syllables < GATES.minSyllablesForRate) {
      flags.push("TOO_FEW_SYLLABLES");
    } else if (phonationSec > 0 && durationSec > 0) {
      syllablesPerSecond = syllables / phonationSec;
      wordsPerMinute = (words / durationSec) * 60;
      articulationRate = (words / phonationSec) * 60;
    }
  }

  return {
    durationSec,
    phonationSec,
    syllablesPerSecond,
    wordsPerMinute,
    articulationRate,
    pauseCount: pauses.length,
    pauseRatio: durationSec > 0 ? pausedSec / durationSec : 0,
    longestPauseMs: pauses.reduce((max, p) => Math.max(max, p.durationSec * 1000), 0),
    rmsDbMean,
    rmsDbP90,
    dynamicRangeDb,
    f0MedianHz,
    f0P10Hz,
    f0P90Hz,
    f0RangeSemitones,
    f0CvPct,
    voicedRatio,
    flags,
  };
}
